import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { USER_ROLES } from '../firebase/config';

// Funzioni base per gli utenti
export const createUserProfile = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      ...userData,
      role: USER_ROLES.CUSTOMER,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Errore nella creazione del profilo utente:', error);
    throw error;
  }
};

export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId, 'profile', 'details');
    const userSnap = await getDoc(userRef);
    return userSnap.exists() ? userSnap.data() : null;
  } catch (error) {
    console.error('Errore nel recupero del profilo utente:', error);
    throw error;
  }
};

// Funzioni per i dettagli del profilo
export const updateUserDetails = async (userId, userData) => {
  try {
    const userRef = doc(db, 'users', userId, 'profile', 'details');
    await setDoc(userRef, {
      ...userData,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Errore nell\'aggiornamento dei dettagli utente:', error);
    throw error;
  }
};

export const getUserDetails = async (userId) => {
  try {
    const detailsRef = doc(db, 'users', userId, 'profile', 'details');
    const detailsSnap = await getDoc(detailsRef);
    return detailsSnap.exists() ? detailsSnap.data() : null;
  } catch (error) {
    console.error('Errore nel recupero dei dettagli utente:', error);
    throw error;
  }
};

// Funzioni per gli indirizzi
export const addAddress = async (userId, address) => {
  try {
    const addressRef = doc(collection(db, 'users', userId, 'addresses'));
    await setDoc(addressRef, {
      ...address,
      createdAt: new Date().toISOString()
    });
    return addressRef.id;
  } catch (error) {
    console.error('Errore nell\'aggiunta dell\'indirizzo:', error);
    throw error;
  }
};

export const getUserAddresses = async (userId) => {
  try {
    const addressesRef = collection(db, 'users', userId, 'addresses');
    const addressesSnap = await getDocs(addressesRef);
    return addressesSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Errore nel recupero degli indirizzi:', error);
    throw error;
  }
};

// Funzioni per gli ordini
export const createOrder = async (userId, orderData) => {
  try {
    const orderRef = doc(collection(db, 'users', userId, 'orders'));
    await setDoc(orderRef, {
      ...orderData,
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return orderRef.id;
  } catch (error) {
    console.error('Errore nella creazione dell\'ordine:', error);
    throw error;
  }
};

export const getUserOrders = async (userId) => {
  try {
    const ordersRef = collection(db, 'users', userId, 'orders');
    const ordersQuery = query(ordersRef, orderBy('createdAt', 'desc'));
    const ordersSnap = await getDocs(ordersQuery);
    return ordersSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Errore nel recupero degli ordini:', error);
    throw error;
  }
};

// Funzioni per i pagamenti
export const addPaymentMethod = async (userId, paymentData) => {
  try {
    const paymentRef = doc(collection(db, 'users', userId, 'payments'));
    await setDoc(paymentRef, {
      ...paymentData,
      createdAt: new Date().toISOString()
    });
    return paymentRef.id;
  } catch (error) {
    console.error('Errore nell\'aggiunta del metodo di pagamento:', error);
    throw error;
  }
};

export const getUserPaymentMethods = async (userId) => {
  try {
    const paymentsRef = collection(db, 'users', userId, 'payments');
    const paymentsSnap = await getDocs(paymentsRef);
    return paymentsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Errore nel recupero dei metodi di pagamento:', error);
    throw error;
  }
};

// Funzioni per le notifiche
export const addNotification = async (userId, notification) => {
  try {
    const notificationRef = doc(collection(db, 'users', userId, 'notifications'));
    await setDoc(notificationRef, {
      ...notification,
      read: false,
      createdAt: new Date().toISOString()
    });
    return notificationRef.id;
  } catch (error) {
    console.error('Errore nell\'aggiunta della notifica:', error);
    throw error;
  }
};

export const getUserNotifications = async (userId) => {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const notificationsQuery = query(notificationsRef, orderBy('createdAt', 'desc'));
    const notificationsSnap = await getDocs(notificationsQuery);
    return notificationsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Errore nel recupero delle notifiche:', error);
    throw error;
  }
};

// Funzioni di utilità
export const updateUserRole = async (userId, newRole) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role: newRole,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    console.error('Errore nell\'aggiornamento del ruolo utente:', error);
    throw error;
  }
};

export const isAdmin = async (userId) => {
  const userProfile = await getUserProfile(userId);
  return userProfile?.role === 'admin';
};

export const isSeller = async (userId) => {
  const userProfile = await getUserProfile(userId);
  return userProfile?.role === 'seller';
};

export const isCustomer = async (userId) => {
  const userProfile = await getUserProfile(userId);
  return userProfile?.role === 'customer';
}; 